"use client";

import { useEffect, useRef, useState, useCallback } from "react";
import { useRouter } from "next/navigation";
import { cn } from "@/lib/utils";
import { MAKER_TOUR_STEPS, LOVER_TOUR_STEPS, GUEST_TOUR_STEPS, type TourStep } from "./tour-steps";
import { StepIcon } from "./tour-icon-map";
import { useAuth } from "@/contexts/auth-context";
import { useMediaPlayer } from "@/contexts/media-player-context";
import {
  X, ChevronLeft, ChevronRight, SkipForward, LogIn,
  // Sidebar nav icons — used in SidebarAnimation illustrations
  Home, UserCircle2, LayoutGrid, ShoppingCart, MessageSquare,
  Radio, Vault, Music, Headphones, BarChart, BarChart2, Mic2, Wand2,
} from "lucide-react";

// Duration of tooltip exit animation before the step content swaps
const EXIT_MS = 160;

// ─── Types ────────────────────────────────────────────────────────────────────

interface SpotlightRect {
  top: number;
  left: number;
  width: number;
  height: number;
}

interface GuidedTourProps {
  startStep?: number;
  onClose: () => void;
}

// ─── Mini animations ─────────────────────────────────────────────────────────

function PulseRing({ color = "#6366f1" }: { color?: string }) {
  return (
    <div className="absolute inset-0 pointer-events-none overflow-visible">
      {[0, 1, 2].map(i => (
        <div
          key={i}
          className="absolute inset-0 rounded-xl border-2 opacity-0"
          style={{ borderColor: color, animation: `tour-pulse 2s ${i * 0.6}s ease-out infinite` }}
        />
      ))}
    </div>
  );
}

// Sidebar illustration icons — match exactly what's in sidebar-nav.tsx
const SIDEBAR_LOVER_ICONS  = [Home, Mic2, Music, ShoppingCart, Radio, MessageSquare, UserCircle2];
const SIDEBAR_MAKER_ICONS  = [Vault, BarChart, Wand2, ShoppingCart, Radio, MessageSquare, UserCircle2];
const SIDEBAR_GUEST_ICONS  = [Home, Mic2, Music, ShoppingCart, Radio, LayoutGrid, UserCircle2];

function SidebarAnimationBase({ icons }: { icons: typeof SIDEBAR_LOVER_ICONS }) {
  return (
    <div className="flex gap-3 items-center mt-4 select-none">
      <div className="flex flex-col gap-1.5">
        {icons.map((Icon, i) => (
          <div
            key={i}
            className="w-8 h-8 rounded-lg flex items-center justify-center"
            style={{
              background: i === 0 ? "hsl(var(--primary))" : "hsl(var(--muted))",
              animation: `tour-highlight-item 3.5s ${i * 0.25}s ease-in-out infinite`,
            }}
          >
            <Icon
              className="h-4 w-4"
              style={{ color: i === 0 ? "hsl(var(--primary-foreground))" : "hsl(var(--muted-foreground))" }}
              strokeWidth={1.75}
            />
          </div>
        ))}
      </div>
      <div className="text-xs text-muted-foreground">← Navigate here</div>
    </div>
  );
}

function PlayerAnimation() {
  const [playing, setPlaying] = useState(false);
  useEffect(() => {
    const t = setInterval(() => setPlaying(p => !p), 2000);
    return () => clearInterval(t);
  }, []);
  return (
    <div className="mt-3 bg-muted/60 rounded-xl p-3 flex items-center gap-3">
      <div className="w-9 h-9 rounded-lg bg-primary/20 flex items-center justify-center"
        style={{ animation: "tour-pulse-button 1.5s ease-in-out infinite" }}>
        <span className="text-lg">{playing ? "⏸" : "▶"}</span>
      </div>
      <div className="flex-1 min-w-0">
        <div className="h-1.5 bg-muted rounded-full overflow-hidden">
          <div className="h-full bg-primary rounded-full"
            style={{ width: playing ? "60%" : "30%", transition: "width 2s linear" }} />
        </div>
        <div className="flex justify-between text-[10px] text-muted-foreground mt-0.5">
          <span>1:23</span><span>3:45</span>
        </div>
      </div>
      <div className="flex items-end gap-px h-6">
        {Array.from({ length: 12 }).map((_, i) => (
          <div key={i} className="w-1 bg-primary/70 rounded-sm"
            style={{
              height: `${30 + Math.sin(i * 0.8) * 50}%`,
              animation: playing ? `tour-wave-bar ${0.4 + (i % 3) * 0.15}s ${i * 0.05}s ease-in-out infinite alternate` : "none",
            }}
          />
        ))}
      </div>
    </div>
  );
}

function VuMeterAnimation() {
  return (
    <div className="mt-3 flex gap-2 items-end h-16 bg-muted/40 rounded-lg p-2">
      {["L", "R"].map(ch => (
        <div key={ch} className="flex flex-col items-center gap-1">
          <div className="w-3 h-12 bg-muted rounded overflow-hidden flex flex-col-reverse">
            <div className="w-full bg-gradient-to-t from-green-500 via-yellow-400 to-red-500 rounded"
              style={{ height: "65%", animation: "tour-vu-meter 0.8s ease-in-out infinite alternate" }}
            />
          </div>
          <span className="text-[9px] text-muted-foreground font-mono">{ch}</span>
        </div>
      ))}
      <div className="flex flex-col gap-0.5 justify-center ml-1">
        {["📊 Spectrum", "〰 Scope", "🌊 Gram", "🎯 Gonio"].map(l => (
          <div key={l} className="text-[9px] text-muted-foreground">{l}</div>
        ))}
      </div>
    </div>
  );
}

function FavoritesAnimation() {
  const items = ["🎵", "🎤", "💿", "🎵", "🎤"];
  return (
    <div className="mt-3 flex gap-2 overflow-hidden">
      {items.map((icon, i) => (
        <div key={i}
          className="flex-shrink-0 w-10 h-10 rounded-lg bg-muted/60 flex items-center justify-center text-lg border border-border/50"
          style={{ animation: `tour-fav-card ${1 + i * 0.2}s ease-in-out infinite alternate` }}
        >
          {icon}
        </div>
      ))}
    </div>
  );
}

// StepLucideIcon is now the shared StepIcon from tour-icon-map.tsx —
// same component used in HelpSection for full icon consistency.

function StepIllustration({ stepId }: { stepId: string }) {
  // Sidebar animation — picks icon set matching the tour type
  if (stepId === "maker-sidebar") return <SidebarAnimationBase icons={SIDEBAR_MAKER_ICONS} />;
  if (stepId === "lover-sidebar" || stepId === "sidebar") return <SidebarAnimationBase icons={SIDEBAR_LOVER_ICONS} />;
  if (stepId === "guest-sidebar") return <SidebarAnimationBase icons={SIDEBAR_GUEST_ICONS} />;
  // Player / meter / favourites animations
  if (stepId === "media-player" || stepId === "lover-player") return <PlayerAnimation />;
  if (stepId === "audio-meters") return <VuMeterAnimation />;
  if (stepId === "favorites-bar" || stepId === "lover-favorites") return <FavoritesAnimation />;
  return null;
}

// ─── Positioning helpers ──────────────────────────────────────────────────────

/**
 * Compute a safe tooltip position for desktop.
 * Uses actual measured tooltip height (from ResizeObserver) instead of a
 * hard-coded estimate so the card never clips out of the viewport.
 */
function computeDesktopTooltipStyle(
  spotlight: SpotlightRect | null,
  position: TourStep["position"],
  tooltipW: number,
  tooltipH: number,
): React.CSSProperties {
  const GAP = 14;
  const EDGE = 12; // min distance from screen edge

  if (!spotlight || position === "center") {
    return { position: "fixed", top: "50%", left: "50%", transform: "translate(-50%, -50%)" };
  }

  const vw = window.innerWidth;
  const vh = window.innerHeight;

  const clampLeft = (l: number) => Math.max(EDGE, Math.min(l, vw - tooltipW - EDGE));
  const clampTop  = (t: number) => Math.max(EDGE, Math.min(t, vh - tooltipH - EDGE));

  const elRight   = spotlight.left + spotlight.width;
  const elBottom  = spotlight.top  + spotlight.height;
  const elCenterX = spotlight.left + spotlight.width  / 2;
  const elCenterY = spotlight.top  + spotlight.height / 2;

  // For each preferred side, try it; if it would overlap the target element
  // or go out of bounds, flip to the opposite side, then fall back to center.
  switch (position) {
    case "right": {
      if (elRight + GAP + tooltipW <= vw - EDGE) {
        return { position: "fixed", top: clampTop(elCenterY - tooltipH / 2), left: elRight + GAP };
      }
      // Flip left
      if (spotlight.left - GAP - tooltipW >= EDGE) {
        return { position: "fixed", top: clampTop(elCenterY - tooltipH / 2), left: spotlight.left - GAP - tooltipW };
      }
      // Fall through to bottom
      return { position: "fixed", top: clampTop(elBottom + GAP), left: clampLeft(elCenterX - tooltipW / 2) };
    }
    case "left": {
      if (spotlight.left - GAP - tooltipW >= EDGE) {
        return { position: "fixed", top: clampTop(elCenterY - tooltipH / 2), left: spotlight.left - GAP - tooltipW };
      }
      if (elRight + GAP + tooltipW <= vw - EDGE) {
        return { position: "fixed", top: clampTop(elCenterY - tooltipH / 2), left: elRight + GAP };
      }
      return { position: "fixed", top: clampTop(elBottom + GAP), left: clampLeft(elCenterX - tooltipW / 2) };
    }
    case "bottom":
      return { position: "fixed", top: clampTop(elBottom + GAP), left: clampLeft(elCenterX - tooltipW / 2) };
    case "top":
      return { position: "fixed", top: clampTop(spotlight.top - tooltipH - GAP), left: clampLeft(elCenterX - tooltipW / 2) };
    default:
      return { position: "fixed", top: "50%", left: "50%", transform: "translate(-50%,-50%)" };
  }
}

/** Arrow that points from the tooltip toward the highlighted element (desktop only) */
function computeArrowStyle(
  spotlight: SpotlightRect | null,
  position: TourStep["position"],
): React.CSSProperties | null {
  if (!spotlight || position === "center") return null;
  const base: React.CSSProperties = {
    position: "absolute",
    width: 10, height: 10,
    background: "hsl(var(--background))",
    border: "1px solid hsl(var(--border))",
    transform: "rotate(45deg)",
  };
  switch (position) {
    case "right":  return { ...base, left: -6,  top: 24, borderRight: "none", borderTop: "none" };
    case "left":   return { ...base, right: -6, top: 24, borderLeft: "none",  borderBottom: "none" };
    case "bottom": return { ...base, top: -6,   left: "50%", marginLeft: -5, borderBottom: "none", borderRight: "none" };
    case "top":    return { ...base, bottom: -6, left: "50%", marginLeft: -5, borderTop: "none",   borderLeft: "none" };
    default: return null;
  }
}

// ─── Main component ───────────────────────────────────────────────────────────

/** Account types that see the Music Maker tour */
const MAKER_TYPES = new Set(["creator", "venue", "admin", "superadmin"]);

export function GuidedTour({ startStep = 0, onClose }: GuidedTourProps) {
  const { user, userProfile } = useAuth();
  const { isActive: isPlayerActive } = useMediaPlayer();
  const router = useRouter();

  const isMaker = !!(userProfile?.accountType && MAKER_TYPES.has(userProfile.accountType));
  const steps = !user
    ? GUEST_TOUR_STEPS
    : isMaker
      ? MAKER_TOUR_STEPS
      : LOVER_TOUR_STEPS;

  const [stepIndex, setStepIndex]         = useState(startStep);
  const [spotlight, setSpotlight]         = useState<SpotlightRect | null>(null);
  const [tooltipVisible, setTooltipVisible] = useState(true);
  const [spotlightFlash, setSpotlightFlash] = useState(false);
  // Actual measured dimensions of the tooltip card
  const [tooltipSize, setTooltipSize]     = useState({ w: 320, h: 300 });
  const [isMobile, setIsMobile]           = useState(false);

  const tooltipRef  = useRef<HTMLDivElement>(null);
  const rafRef      = useRef(0);
  const navLockRef  = useRef(false);

  const step: TourStep = steps[stepIndex];
  const isFirst = stepIndex === 0;
  const isLast  = stepIndex === steps.length - 1;

  // Detect mobile on mount + resize
  useEffect(() => {
    const check = () => setIsMobile(window.innerWidth < 640);
    check();
    window.addEventListener("resize", check);
    return () => window.removeEventListener("resize", check);
  }, []);

  // Measure actual tooltip card size via ResizeObserver
  useEffect(() => {
    if (!tooltipRef.current) return;
    const ro = new ResizeObserver(entries => {
      const e = entries[0];
      if (e) {
        setTooltipSize({ w: Math.ceil(e.contentRect.width), h: Math.ceil(e.contentRect.height) });
      }
    });
    ro.observe(tooltipRef.current);
    return () => ro.disconnect();
  }, []);

  /** Animated step navigation: fade tooltip out → swap content → fade in */
  const navigateStep = useCallback((nextIndex: number) => {
    if (navLockRef.current) return;
    navLockRef.current = true;
    setTooltipVisible(false);
    setTimeout(() => {
      setStepIndex(nextIndex);
      setTooltipVisible(true);
      setSpotlightFlash(true);
      setTimeout(() => setSpotlightFlash(false), 700);
      navLockRef.current = false;
    }, EXIT_MS);
  }, []);

  // Track target element rect (RAF loop).
  // Only call setSpotlight when the rounded coordinates actually change so we
  // don't trigger a re-render every frame — frequent re-renders with an inline
  // CardContent function were destroying button DOM nodes faster than clicks
  // could complete (mousedown → node destroyed → mouseup lands on nothing).
  const spotlightCacheRef = useRef<SpotlightRect | null>(null);

  const updateSpotlight = useCallback(() => {
    if (!step.target) {
      if (spotlightCacheRef.current !== null) { spotlightCacheRef.current = null; setSpotlight(null); }
      return;
    }
    const el = document.querySelector(`[data-tour="${step.target}"]`);
    if (!el) {
      if (spotlightCacheRef.current !== null) { spotlightCacheRef.current = null; setSpotlight(null); }
      return;
    }
    const r = el.getBoundingClientRect();
    const PAD = 6;
    const next: SpotlightRect = {
      top:    Math.round(r.top  - PAD),
      left:   Math.round(r.left - PAD),
      width:  Math.round(r.width  + PAD * 2),
      height: Math.round(r.height + PAD * 2),
    };
    const prev = spotlightCacheRef.current;
    if (!prev || prev.top !== next.top || prev.left !== next.left ||
        prev.width !== next.width || prev.height !== next.height) {
      spotlightCacheRef.current = next;
      setSpotlight(next);
    }
  }, [step.target]);

  useEffect(() => {
    spotlightCacheRef.current = null; // reset cache on step change
    updateSpotlight();
    const loop = () => { updateSpotlight(); rafRef.current = requestAnimationFrame(loop); };
    rafRef.current = requestAnimationFrame(loop);
    return () => cancelAnimationFrame(rafRef.current);
  }, [updateSpotlight]);

  // Keyboard nav
  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if (e.key === "Escape")     onClose();
      if (e.key === "ArrowRight" && !isLast)  navigateStep(stepIndex + 1);
      if (e.key === "ArrowLeft"  && !isFirst) navigateStep(stepIndex - 1);
    };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, [isFirst, isLast, stepIndex, onClose, navigateStep]);

  // ── Compute desktop tooltip position ──────────────────────────────────────
  const desktopStyle = computeDesktopTooltipStyle(
    spotlight,
    step.position,
    tooltipSize.w || 320,
    tooltipSize.h || 300,
  );
  const arrow = isMobile ? null : computeArrowStyle(spotlight, step.position);

  const progress = ((stepIndex + 1) / steps.length) * 100;

  // ─── Shared card content (used in both mobile + desktop) ─────────────────

  function CardContent() {
    return (
      <>
        {/* Progress bar */}
        <div className="h-1 bg-muted">
          <div className="h-full bg-primary transition-all duration-500 rounded-full" style={{ width: `${progress}%` }} />
        </div>

        {/* Header */}
        <div className="flex items-start justify-between px-4 pt-4 pb-2 sm:px-5">
          <div className="flex items-center gap-2.5">
            <StepIcon stepId={step.id} size={22} />
            <h3 className="text-base font-bold leading-tight text-foreground">{step.title}</h3>
          </div>
          <button
            onClick={onClose}
            className="text-muted-foreground hover:text-foreground transition-colors ml-2 flex-shrink-0 mt-0.5 p-1 -m-1"
            aria-label="Close tour"
          >
            <X className="h-4 w-4" />
          </button>
        </div>

        {/* Body */}
        <div className="px-4 pb-2 sm:px-5 overflow-y-auto" style={{ maxHeight: isMobile ? "30vh" : undefined }}>
          <p className="text-sm text-muted-foreground leading-relaxed">{step.description}</p>
          <StepIllustration stepId={step.id} />
          {step.cta && (
            <button
              onClick={() => { onClose(); router.push(step.cta!.href); }}
              className="mt-3 w-full flex items-center justify-center gap-2 h-10 rounded-xl bg-primary text-primary-foreground text-sm font-semibold hover:bg-primary/90 transition-colors"
            >
              <LogIn className="h-4 w-4" />
              {step.cta.label}
            </button>
          )}
        </div>

        {/* Navigation footer */}
        <div className="flex items-center justify-between px-4 py-3 sm:px-5 border-t border-border bg-muted/30">
          <span className="text-xs text-muted-foreground font-mono">{stepIndex + 1} / {steps.length}</span>
          <div className="flex items-center gap-1.5">
            {!isFirst && (
              <button
                onClick={() => navigateStep(stepIndex - 1)}
                className="h-8 px-3 rounded-lg border border-border text-sm text-muted-foreground hover:bg-muted flex items-center gap-1 transition-colors"
              >
                <ChevronLeft className="h-3.5 w-3.5" /> Back
              </button>
            )}
            {!isLast ? (
              <>
                <button
                  onClick={onClose}
                  className="h-8 px-3 rounded-lg border border-border text-xs text-muted-foreground hover:bg-muted flex items-center gap-1 transition-colors"
                >
                  <SkipForward className="h-3 w-3" /> Skip
                </button>
                <button
                  onClick={() => navigateStep(stepIndex + 1)}
                  className="h-8 px-4 rounded-lg bg-primary text-primary-foreground text-sm font-semibold hover:bg-primary/90 flex items-center gap-1 transition-colors"
                >
                  Next <ChevronRight className="h-3.5 w-3.5" />
                </button>
              </>
            ) : (
              <button
                onClick={onClose}
                className="h-8 px-4 rounded-lg bg-primary text-primary-foreground text-sm font-semibold hover:bg-primary/90 transition-colors"
              >
                {step.cta ? "Maybe Later" : "Finish 🎉"}
              </button>
            )}
          </div>
        </div>
      </>
    );
  }

  return (
    <>
      {/* ── Keyframe styles ── */}
      <style>{`
        @keyframes tour-pulse {
          0%   { transform: scale(1);    opacity: 0.6; }
          100% { transform: scale(1.18); opacity: 0; }
        }
        @keyframes tour-highlight-item {
          0%,70%,100% { background: hsl(var(--muted)); transform: scale(1); }
          35%          { background: hsl(var(--primary) / 0.8); transform: scale(1.1); }
        }
        @keyframes tour-pulse-button {
          0%,100% { box-shadow: 0 0 0 0 hsl(var(--primary) / 0.5); }
          50%      { box-shadow: 0 0 0 8px hsl(var(--primary) / 0); }
        }
        @keyframes tour-wave-bar {
          from { transform: scaleY(0.4); }
          to   { transform: scaleY(1); }
        }
        @keyframes tour-vu-meter {
          from { height: 40%; }
          to   { height: 85%; }
        }
        @keyframes tour-fav-card {
          from { transform: translateY(0); opacity: 0.7; }
          to   { transform: translateY(-4px); opacity: 1; }
        }
        @keyframes tour-spotlight-breathe {
          0%   { box-shadow: 0 0 0 3px hsl(var(--primary) / 0.18), 0 0 18px 0 hsl(var(--primary) / 0.10); }
          45%  { box-shadow: 0 0 0 5px hsl(var(--primary) / 0.55), 0 0 28px 4px hsl(var(--primary) / 0.28); }
          100% { box-shadow: 0 0 0 3px hsl(var(--primary) / 0.18), 0 0 18px 0 hsl(var(--primary) / 0.10); }
        }
        @keyframes tour-spotlight-border {
          0%,100% { border-color: hsl(var(--primary) / 0.55); }
          50%     { border-color: hsl(var(--primary)); }
        }
        @keyframes tour-spotlight-flash {
          0%   { transform: scale(1);    opacity: 0.9; }
          60%  { transform: scale(1.06); opacity: 0.3; }
          100% { transform: scale(1.1);  opacity: 0; }
        }
        @keyframes tour-tooltip-in {
          from { opacity: 0; transform: translateY(8px) scale(0.97); }
          to   { opacity: 1; transform: translateY(0)   scale(1); }
        }
        @keyframes tour-tooltip-out {
          from { opacity: 1; transform: translateY(0)   scale(1); }
          to   { opacity: 0; transform: translateY(6px) scale(0.97); }
        }
        /* Mobile bottom-sheet slide animation */
        @keyframes tour-sheet-in {
          from { transform: translateY(100%); }
          to   { transform: translateY(0); }
        }
        @keyframes tour-sheet-out {
          from { transform: translateY(0); }
          to   { transform: translateY(100%); }
        }
      `}</style>

      {/* ── Spotlight: breathing outline + flash-in ring ── */}
      {spotlight && (
        <div className="fixed inset-0 z-[200] pointer-events-none">
          <div
            className="absolute rounded-xl pointer-events-none"
            style={{
              top:    spotlight.top,
              left:   spotlight.left,
              width:  spotlight.width,
              height: spotlight.height,
              border: "2px solid hsl(var(--primary))",
              animation: "tour-spotlight-breathe 2.6s ease-in-out infinite, tour-spotlight-border 2.6s ease-in-out infinite",
            }}
          >
            {step.animation === "pulse" && <PulseRing />}
          </div>
          {spotlightFlash && (
            <div
              className="absolute rounded-xl pointer-events-none"
              style={{
                top:    spotlight.top,
                left:   spotlight.left,
                width:  spotlight.width,
                height: spotlight.height,
                border: "3px solid hsl(var(--primary))",
                animation: `tour-spotlight-flash 700ms ease-out forwards`,
              }}
            />
          )}
        </div>
      )}

      {/* ── MOBILE: bottom-sheet card ─────────────────────────────────────────
          Always anchored to the bottom edge — never covers the highlighted
          element above, never bleeds off screen sides.
      ── */}
      {isMobile && (
        <div
          ref={tooltipRef}
          className={cn(
            "fixed inset-x-0 z-[201] rounded-t-2xl bg-background border-t border-x border-border shadow-2xl overflow-hidden",
            // Always sit above the media player bar (56px) + chat button (also 56px when player active).
            // When no player: chat FAB is ~50px so 56px still clears it. Hardcoded per CLAUDE.md rule.
            isPlayerActive ? "bottom-[56px]" : "bottom-[50px]",
          )}
          style={{
            animation: tooltipVisible
              ? `tour-sheet-in ${EXIT_MS + 60}ms cubic-bezier(0.32,0.72,0,1) forwards`
              : `tour-sheet-out ${EXIT_MS}ms ease-in forwards`,
          }}
          onClick={e => e.stopPropagation()}
        >
          {/* Drag handle */}
          <div className="flex justify-center pt-2.5 pb-1">
            <div className="w-10 h-1 rounded-full bg-muted-foreground/25" />
          </div>

          {/* Dot progress indicator (replaces floating dots at bottom) */}
          <div className="flex justify-center gap-1.5 mb-1">
            {steps.map((_, i) => (
              <button
                key={i}
                onClick={() => navigateStep(i)}
                className={cn(
                  "rounded-full transition-all",
                  i === stepIndex ? "w-5 h-2 bg-primary"
                    : i < stepIndex ? "w-2 h-2 bg-primary/50"
                    : "w-2 h-2 bg-muted-foreground/30"
                )}
                aria-label={`Step ${i + 1}`}
              />
            ))}
          </div>

          {/* Call as plain function — NOT <CardContent /> — so React never
              unmounts/remounts the button DOM nodes across re-renders. */}
          {CardContent()}
        </div>
      )}

      {/* ── DESKTOP: floating tooltip card ───────────────────────────────────
          Positioned away from the highlighted element using measured height.
          Clamped to viewport edges; arrow points toward the target.
      ── */}
      {!isMobile && (
        <div
          ref={tooltipRef}
          className="fixed z-[201] w-[320px]"
          style={{
            ...desktopStyle,
            animation: tooltipVisible
              ? `tour-tooltip-in ${EXIT_MS + 60}ms cubic-bezier(0.22,1,0.36,1) forwards`
              : `tour-tooltip-out ${EXIT_MS}ms ease-in forwards`,
          }}
          onClick={e => e.stopPropagation()}
        >
          {/* Arrow */}
          {arrow && <div style={arrow} />}

          <div className="bg-background border border-border rounded-2xl shadow-2xl overflow-hidden">
            {/* Call as plain function — NOT <CardContent /> — so React never
                unmounts/remounts the button DOM nodes across re-renders. */}
            {CardContent()}
          </div>
        </div>
      )}

      {/* ── Desktop dot nav (not shown on mobile — dots are inside the sheet) ── */}
      {!isMobile && (
        <div className="fixed z-[201] bottom-6 left-1/2 -translate-x-1/2 flex gap-1.5">
          {steps.map((_, i) => (
            <button
              key={i}
              onClick={() => navigateStep(i)}
              className={cn(
                "rounded-full transition-all",
                i === stepIndex ? "w-5 h-2 bg-primary"
                  : i < stepIndex ? "w-2 h-2 bg-primary/50"
                  : "w-2 h-2 bg-muted-foreground/30"
              )}
              aria-label={`Go to step ${i + 1}`}
            />
          ))}
        </div>
      )}
    </>
  );
}
